'use client';

import { useTranslations } from 'next-intl';
import { Loader2, type LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { GoalsEvaluation } from '@/types/project';

// Standard `.warning` notice palette (amber), mirrored from the shared
// NoticeBanner so the model's brief feedback reads as the same family — but
// icon-less and with a caret pointing at the quality indicator.
const WARNING_SURFACE = 'bg-[#FBFBE4] dark:bg-amber-900/20';
const WARNING_BORDER =
  'border-[rgba(217,119,6,0.18)] dark:border-[rgba(180,83,9,0.45)]';
const WARNING_TEXT = 'text-[#594D33] dark:text-amber-400';

interface GoalsEvaluationFeedbackProps {
  evaluation?: GoalsEvaluation | null;
  pending?: boolean;
  /** Draw an upward caret on the card's top-right edge that points at the brief
   *  quality indicator above it (the Brief screen layout). */
  tail?: boolean;
}

/**
 * Server-side evaluation of the whole brief — text AND photos — shown verbatim
 * from the model. Rendered as a standard `.warning` notice (amber palette,
 * shared with NoticeBanner) but with NO leading icon and an upward caret that
 * points at the brief quality indicator above it. Texts come verbatim from the
 * API (already localized + tone-shaped). Shared shape with Apple/Android.
 */
export function GoalsEvaluationFeedback({
  evaluation,
  pending,
  tail = false,
}: GoalsEvaluationFeedbackProps) {
  const t = useTranslations('project');

  if (pending) {
    return (
      <ModelSpeechCard icon={Loader2} spin tail={tail}>
        <p className={cn('text-notice-body leading-[1.3]', WARNING_TEXT)}>
          {t('goalsEvaluating')}
        </p>
      </ModelSpeechCard>
    );
  }

  const suggestions = evaluation?.suggestions ?? [];
  if (suggestions.length === 0) return null;

  return (
    <ModelSpeechCard tail={tail}>
      {suggestions.map((suggestion, i) => (
        <p key={i} className={cn('text-notice-body leading-[1.3]', WARNING_TEXT)}>
          {suggestion}
        </p>
      ))}
    </ModelSpeechCard>
  );
}

/**
 * The model's brief feedback as a standard `.warning` notice (amber palette,
 * shared with NoticeBanner) — but icon-less, and with an upward caret on its
 * top-right edge that points at the quality indicator above. The caret apex
 * centers 24px from the right edge, matching the indicator's trailing inset so
 * it points vertically straight at it. Only the transient "evaluating" state
 * shows a spinner.
 */
function ModelSpeechCard({
  icon: Icon,
  spin = false,
  tail = false,
  children,
}: {
  icon?: LucideIcon;
  spin?: boolean;
  tail?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('relative rounded-2xl border p-4', WARNING_SURFACE, WARNING_BORDER)}>
      {/* Upward caret on the top-right edge, pointing at the brief quality
          indicator above. A rotated square with its two top edges bordered; its
          fill covers the card's top border underneath so the junction is seamless.
          right-[18px] → caret center sits 24px from the right edge (12px box,
          centered), aligned with the indicator's matching trailing inset. */}
      {tail && (
        <span
          aria-hidden
          className={cn(
            'absolute -top-1.5 right-[18px] h-3 w-3 rotate-45 rounded-tl-[2px] border-l border-t',
            WARNING_SURFACE,
            WARNING_BORDER
          )}
        />
      )}
      <div className="flex items-start gap-2.5">
        {Icon && (
          <Icon
            className={cn(
              'h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400',
              spin && 'animate-spin'
            )}
          />
        )}
        <div className="min-w-0 flex-1 space-y-2">{children}</div>
      </div>
    </div>
  );
}
